// ============================================================================
// navigate-link — SINGLE-DESTINATION navigation handoff.
//   CW-C Phase 1 (2026-08-04): address-first.
//   CW-C Phase 4 (2026-08-05): COORDINATE-FIRST WHEN TRUSTED. Ben's Option 1.
//
// HARD RULE, unchanged:
//   * Route MATH stays on COORDINATES — waypoint ordering, haversine in
//     `route-order.ts`, and the multi-stop URLs in `route-links.ts`.
//   * This file is single-destination NAVIGATION only. Different job.
//
// ---------------------------------------------------------------------------
// WHY THE PHASE 1 REASONING WAS REPLACED
//
// Phase 1 sent the ADDRESS and let the provider geocode at tap time, on the
// theory that a live geocode beats a stored coordinate of unknown provenance.
// The Phase 3 dry-run appeared to support that. It did not, and the finding was
// retracted on 2026-08-05: 14 of 20 legacy coordinates matched a fresh Google
// geocode to SEVEN DECIMAL PLACES, which is not two measurements agreeing — it
// is one source read twice. The legacy coordinates were themselves Google
// output. Δ = 0 measured shared provenance, not accuracy.
//
// The field failure that actually happened (FAC-0133 Old Tesson, 2026-08-04
// 09:00) was a LINK-FORMAT bug: the app emitted `https://maps.apple.com/?q=…`,
// which is a Maps SEARCH. Apple resolved that search to a nearby named entity
// 721 m / 0.45 mi north-east and the driver ended up at a warehouse. The
// coordinate was correct the whole time; the URL threw it away.
//
// Lesson encoded here: a live geocode is a GUESS made at tap time by a party
// that has never seen the building. A trusted stored coordinate is a FACT
// someone established once. Prefer the fact. Guess only when there is no fact.
//
// ---------------------------------------------------------------------------
// WHAT "TRUSTED" MEANS — and why it is deliberately narrow
//
// `coordTrusted` is NOT "we have a coordinate". It is "this coordinate has
// recorded, building-level provenance". Today that means
// geocode_precision = 'ROOFTOP' (31 of 206 non-closed facilities).
//
// GEOMETRIC_CENTER and APPROXIMATE are EXCLUDED on purpose. They place the pin
// at the centre of a parcel or road segment, and Google's Routes API docs warn
// that a lat/lng "can result in the location being snapped to the road nearest
// to those coordinates — which might not be an access point to the property."
// On a hospital campus that is the difference between the loading dock and the
// front door, so those fall through to the address, where a live geocode of a
// suite-level address is the better guess.
//
// The other 173 facilities carry geocode_source = 'Legacy_Unknown' with NULL
// precision. Per Ben's 2026-08-05 decision (Option C) they are NOT being
// re-geocoded and NOT being stamped ROOFTOP — an unearned label is worse than
// an honest blank. So they stay address-first until something independent of
// Google verifies them. See §6 of cw-c-phase3-pilot-report-2026-08-05.md.
//
// ---------------------------------------------------------------------------
// PROVIDER NOTES
//
// Apple: the unified `/directions` schema (iOS 18.4+) documents `destination`
// as "an address, coordinate, or a place name", and Apple's own example 6 is
// `destination=40.596896,-73.514907`. Note the older `?daddr=lat,lng` form was
// reported broken in 2024 (Apple Developer Forums thread 751832 — Maps "alters
// them to some nearest known entity"), which is the same failure mode Ben hit
// via `?q=`. That is a reason to use the CURRENT schema, never the legacy one,
// and never a search URL.
//
// UNIVERSAL LINKS ONLY. Never comgooglemaps:// or waze://. Apple: "universal
// links are strongly recommended"; Waze documents that with waze:// and the app
// not installed, "nothing happens" — a dead button for a tech in a car park.
// ============================================================================

export type NavProvider = 'apple' | 'google' | 'waze';

export const NAV_PROVIDERS: readonly NavProvider[] = ['apple', 'google', 'waze'];

export const NAV_PROVIDER_LABEL: Record<NavProvider, string> = {
  apple: 'Apple Maps',
  google: 'Google Maps',
  waze: 'Waze',
};

export interface NavTarget {
  /** Routing input when the coordinate is absent or untrusted. */
  address?: string | null;
  /** Preferred routing input when `coordTrusted`. Also the map pin. */
  lat?: number | null;
  lng?: number | null;
  /**
   * True when this coordinate has recorded building-level provenance and may be
   * used as the routing destination. Derive it — do not hand-set it — with
   * `isCoordTrusted(facility.geocode_precision)`. Absent/false means
   * "coordinate of unknown quality": still fine as a map pin, not fine as the
   * thing a driver follows.
   */
  coordTrusted?: boolean;
  /** Human label for the destination (Waze search context, a11y). */
  name?: string | null;
}

/**
 * Precision values that earn coordinate-first routing. Exactly one entry today,
 * and adding to it is a decision, not a tidy-up: GEOMETRIC_CENTER and
 * APPROXIMATE are parcel/road-segment placements and are excluded by design
 * (see the header). RANGE_INTERPOLATED is interpolated between road points,
 * so it is road-level too — also excluded.
 */
export const NAV_TRUSTED_PRECISION: readonly string[] = ['ROOFTOP'];

/** `facilities.coord_verified_result` — a human who physically went there. */
export type CoordVerifiedResult = 'confirmed' | 'wrong' | null | undefined;

/**
 * Whether a stored coordinate may be the routing DESTINATION.
 *
 * Two independent kinds of evidence, and field evidence outranks the geocoder in
 * BOTH directions (CW-C Phase 4 / Option C, 2026-08-05):
 *
 *   1. `verified === 'wrong'`     → never. Someone drove it and it failed. This
 *      beats even ROOFTOP: Google's confidence is not evidence against a person
 *      who stood in the wrong car park.
 *   2. `verified === 'confirmed'` → yes, whatever the precision. This is the only
 *      way the 173 `Legacy_Unknown` facilities can ever become trusted, because
 *      re-geocoding them would only compare Google against Google — the mistake
 *      that produced the retracted "already accurate" finding.
 *   3. otherwise → fall back to building-level geocoder precision.
 *
 * Mirrors `fn_coord_is_trusted(text,text)` in SQL. CHANGE BOTH OR NEITHER.
 */
export function isCoordTrusted(
  precision: string | null | undefined,
  verified?: CoordVerifiedResult,
): boolean {
  if (verified === 'wrong') return false;
  if (verified === 'confirmed') return true;
  return (
    typeof precision === 'string' &&
    NAV_TRUSTED_PRECISION.includes(precision.trim().toUpperCase())
  );
}

const hasText = (s: string | null | undefined): s is string =>
  typeof s === 'string' && s.trim().length > 0;

const hasCoord = (t: NavTarget): boolean =>
  typeof t.lat === 'number' && typeof t.lng === 'number' &&
  Number.isFinite(t.lat) && Number.isFinite(t.lng);

/** `lat,lng` with no space after the comma — required by all three providers. */
const coordStr = (t: NavTarget): string => `${t.lat},${t.lng}`;

/** True when we can build any usable link at all. */
export function canNavigate(t: NavTarget): boolean {
  return hasText(t.address) || hasCoord(t);
}

/** Which input the provider is given as the destination. */
export type NavDestinationKind = 'coordinate' | 'address';

/**
 * The routing precedence, in one place so the URL builders cannot disagree:
 *   1. trusted coordinate  — a fact someone established
 *   2. address             — a guess the provider makes at tap time
 *   3. untrusted coordinate — better than a dead button
 * Returns null only when the target has neither.
 */
export function navDestinationKind(t: NavTarget): NavDestinationKind | null {
  if (t.coordTrusted === true && hasCoord(t)) return 'coordinate';
  if (hasText(t.address)) return 'address';
  if (hasCoord(t)) return 'coordinate';
  return null;
}

/**
 * Build the single-destination navigation URL for one provider.
 *
 * Returns null when the target has neither an address nor a coordinate — the
 * caller renders the "add an address" prompt instead of a dead button.
 */
export function buildNavigateUrl(provider: NavProvider, t: NavTarget): string | null {
  const kind = navDestinationKind(t);
  if (!kind) return null;
  // Trusted coordinate wins; otherwise the address; otherwise the untrusted
  // coordinate. See navDestinationKind.
  const dest = kind === 'coordinate' ? coordStr(t) : (t.address as string).trim();
  const enc = encodeURIComponent(dest);

  switch (provider) {
    case 'apple':
      // Unified Maps URLs (iOS 18.4 / macOS 15.4+). `mode=driving`, NOT the
      // legacy `dirflg=d`. The legacy `?daddr=` form still 301s server-side,
      // but on-device the universal-link handler intercepts before any network
      // fetch, and Apple has published no deprecation notice either way — so
      // use the current schema. Omitting `source` means "from here".
      // NB: Apple's own published examples contain invalid percent-escapes
      // (`1%Dr.`, `%94102`) — do not copy them; always encodeURIComponent.
      return `https://maps.apple.com/directions?destination=${enc}&mode=driving`;

    case 'google':
      // `api=1` is MANDATORY — without it every other parameter is silently
      // ignored. `dir_action=navigate` only yields turn-by-turn when `origin`
      // is OMITTED: Google's docs state that an origin not close to the
      // device's location "launches a route preview" instead. So never send
      // an origin here.
      return (
        'https://www.google.com/maps/dir/?api=1' +
        `&destination=${enc}` +
        '&travelmode=driving' +
        '&dir_action=navigate'
      );

    case 'waze': {
      // Waze splits the destination across two params, so the precedence has to
      // be expressed by WHICH ONES WE SEND, not by a single `dest` string:
      //   * trusted coordinate → `ll` ONLY. Sending `q` alongside it would hand
      //     Waze a search term that can win over the pin — the same class of bug
      //     as Apple's `?q=`. Omit it.
      //   * address → `q` plus `ll` when we have any coordinate. Waze's own
      //     documented example pairs them, and `q` alone drops the driver into a
      //     search-results list they have to guess from.
      // utm_source is vendor-recommended for partner support.
      const params = new URLSearchParams();
      if (kind === 'coordinate') {
        params.set('ll', coordStr(t));
      } else {
        params.set('q', (t.address as string).trim());
        if (hasCoord(t)) params.set('ll', coordStr(t));
      }
      params.set('navigate', 'yes');
      params.set('utm_source', 'tcs-app');
      return `https://waze.com/ul?${params.toString()}`;
    }
  }
}

/**
 * First-run default only. Once the user picks a provider we persist it and
 * never guess again — UA sniffing cannot carry this feature:
 *   * MDN: parsing the UA "is very hard to do reliably… a last resort".
 *   * navigator.userAgentData is Chromium-only, so it is UNAVAILABLE on iOS
 *     Safari — exactly the case we are trying to detect.
 *   * iPadOS 13+ sends the macOS Catalina UA verbatim and navigator.platform
 *     returns 'MacIntel' (Apple forum 119186, open with no response), hence
 *     the maxTouchPoints probe.
 */
export function detectDefaultProvider(
  ua: string,
  maxTouchPoints = 0,
): NavProvider {
  const isIosLike = /iPad|iPhone|iPod/i.test(ua);
  const isIpadOsMasqueradingAsMac = /Macintosh|Mac OS X/i.test(ua) && maxTouchPoints > 1;
  return isIosLike || isIpadOsMasqueradingAsMac ? 'apple' : 'google';
}
